import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardStats, getUsers, getProjects } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Users, 
  FolderKanban, 
  Clock, 
  CreditCard, 
  MessageSquare,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, projectsRes] = await Promise.all([
        getDashboardStats(),
        getUsers('pending'),
        getProjects()
      ]);
      setStats(statsRes.data);
      setPendingUsers(usersRes.data.slice(0, 5));
      setRecentProjects(projectsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Clients', value: stats?.total_clients || 0, icon: Users, color: 'text-[#4A90D9]' },
    { label: 'Active Projects', value: stats?.total_projects || 0, icon: FolderKanban, color: 'text-[#10B981]' },
    { label: 'Pending Approvals', value: stats?.pending_approvals || 0, icon: Clock, color: 'text-[#F59E0B]' },
    { label: 'Unpaid Projects', value: stats?.pending_payments || 0, icon: CreditCard, color: 'text-[#EF4444]' },
    { label: 'Unread Messages', value: stats?.recent_messages || 0, icon: MessageSquare, color: 'text-[#38BDF8]' },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-slate-400">Welcome to your admin portal overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <div 
            key={stat.label}
            className="stats-card card-hover animate-fade-in-up opacity-0"
            style={{ animationDelay: `${0.1 * index}s` }}
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <div className={`value ${stat.color}`}>{stat.value}</div>
            <div className="label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Approvals */}
        <Card className="bg-[#111827] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#F59E0B]" />
              Pending Approvals
            </CardTitle>
            <Link to="/admin/clients?status=pending">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {pendingUsers.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No pending approvals</p>
            ) : (
              <div className="space-y-3">
                {pendingUsers.map((user) => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#4A90D9]/20 flex items-center justify-center text-[#4A90D9] font-medium">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-sm text-slate-400">{user.email}</p>
                      </div>
                    </div>
                    <Badge className="badge-pending">Pending</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Projects */}
        <Card className="bg-[#111827] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-[#4A90D9]" />
              Recent Projects
            </CardTitle>
            <Link to="/admin/projects">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentProjects.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No projects yet</p>
            ) : (
              <div className="space-y-3">
                {recentProjects.map((project) => (
                  <Link 
                    key={project.id}
                    to={`/admin/projects/${project.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors block"
                  >
                    <div>
                      <p className="text-white font-medium">{project.name}</p>
                      <p className="text-sm text-slate-400">{project.client_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`badge-${project.status}`}>
                        {project.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
